/**
 * Pure helpers for reading a requisition's test/panel selection.
 *
 * Kept free of service/store imports on purpose so the save-blocking rules can
 * be unit tested. Re-exported from `Utils/Common/Requisition` for callers.
 */

/** UI types whose `panels` array holds the requisition's test/panel selection. */
export const PANEL_SELECTION_UI_TYPES = new Set([
  "MultipleSelectablePanel",
  "SelectablePanel",
  "HeaderSelectableOnlyPanel",
  "MultiplerReferenceLabPanelSelection",
]);

/**
 * These fields also carry a `panels` array, but it only groups diagnosis
 * codes - they are not a test selection and must never satisfy the
 * "at least one test or panel" rule.
 */
export const NON_TEST_PANEL_FIELDS = new Set(["ICDPanels", "ComorbidityPanels"]);

/**
 * A `ValidationPanel` renders no input of its own - it exists so a single
 * message can cover a group of sibling fields (e.g. `MedicationPanel` for the
 * Active Medication List).
 */
export const VALIDATION_PANEL_UI_TYPE = "ValidationPanel";

/**
 * Sibling ui types that must not satisfy a ValidationPanel on their own.
 * A prescription photo is supporting evidence, not a medication selection, so
 * uploading one cannot stand in for picking a medication.
 */
export const NON_CONTRIBUTING_PANEL_UI_TYPES = new Set([
  VALIDATION_PANEL_UI_TYPE,
  "File",
]);

/**
 * Emptiness test for a single sibling's contribution to a ValidationPanel.
 *
 * `File` and multi-select controls persist their value as a JSON *string*, so
 * `"[]"` and `"{}"` have to read as empty - treating them as present is what
 * let the medication rule pass with nothing selected.
 */
export const isEmptyPanelContribution = (value: any): boolean => {
  if (value === null || value === undefined) return true;
  if (typeof value === "boolean") return !value;
  if (typeof value === "string") {
    const trimmed = value.trim();
    if (trimmed === "" || trimmed === "false") return true;
    try {
      const parsed = JSON.parse(trimmed);
      if (Array.isArray(parsed)) return parsed.length === 0;
      if (parsed && typeof parsed === "object") return Object.keys(parsed).length === 0;
      if (typeof parsed === "boolean") return !parsed;
    } catch {
      // Not JSON - a genuine string value.
    }
    return false;
  }
  if (Array.isArray(value)) return value.length === 0;
  if (typeof value === "object") return Object.keys(value).length === 0;
  return false;
};

/** A panel counts as selected when its own flag is set or any of its tests is checked. */
export const isPanelSelected = (panel: any): boolean => {
  if (!panel || panel.isVisible === false) return false;
  const hasSelectedTest =
    Array.isArray(panel.testOptions) &&
    panel.testOptions.some((test: any) => test?.isSelected);
  return Boolean(panel.isSelected) || hasSelectedTest;
};

/**
 * Value a field has to be validated against.
 *
 * Panel fields keep their real selection on `panels`, not on `defaultValue`.
 * `defaultValue` cannot be used as a fallback here: the panel handlers write it
 * from the field's full `panels` list (the default-select effect), from a
 * panel's full `testOptions` list, or from a stale `storeChild` state, so it
 * stays non-empty while nothing is actually selected. A non-empty
 * `defaultValue` silently satisfies `min(1)`, which is how Testing Options
 * bypassed its required validation.
 *
 * Deliberately keyed on the four selectable ui types only. Other controls carry
 * a `panels` array while keeping their real value in `defaultValue` -
 * `SearchableDropDown` (Additional Test) builds a synthetic panel there, and
 * `HeaderNonSelectablePanel` panels can never be selected at all - so treating
 * "has a panels array" as the trigger would fail those fields permanently.
 * `NON_TEST_PANEL_FIELDS` are excluded by name for the same reason.
 */
export const getFieldValueForValidation = (
  field: any,
  sectionFields?: any[]
): any => {
  if (!field) return undefined;

  // A ValidationPanel's `defaultValue` is written by its sibling controls'
  // handlers and by edit-mode hydration, so it goes stale: it kept a removed
  // medication after `OtherMedication` was already empty, which silently
  // satisfied "select at least one option" on the first submit and only failed
  // on the second, once something else had reset it. Derive the value from the
  // siblings that are actually on screen instead of trusting the aggregate.
  if (
    field.uiType === VALIDATION_PANEL_UI_TYPE &&
    Array.isArray(sectionFields)
  ) {
    return sectionFields
      .filter(
        (sibling: any) =>
          sibling &&
          sibling !== field &&
          !NON_CONTRIBUTING_PANEL_UI_TYPES.has(sibling.uiType) &&
          sibling.visible !== false
      )
      .map((sibling: any) => getFieldValueForValidation(sibling))
      .filter((value: any) => !isEmptyPanelContribution(value));
  }

  const hasPanels = Array.isArray(field.panels) && field.panels.length > 0;
  const isPanelField =
    PANEL_SELECTION_UI_TYPES.has(field.uiType) &&
    !NON_TEST_PANEL_FIELDS.has(field.systemFieldName);

  if (isPanelField && hasPanels) {
    // A panel counts as selected when its own flag is set or any of its tests
    // is checked - `isSelected` is not always kept in sync with `testOptions`.
    return field.panels.filter(isPanelSelected);
  }

  return field.defaultValue;
};

/** Testing-option field the requisition cannot be completed without. */
export const isRequiredTestSelectionField = (field: any): boolean => {
  if (!field || field.visible === false) return false;
  if (NON_TEST_PANEL_FIELDS.has(field.systemFieldName)) return false;
  if (!PANEL_SELECTION_UI_TYPES.has(field.uiType)) return false;
  if (!Array.isArray(field.panels)) return false;
  const hasValidation =
    typeof field.validationExpression === "string" &&
    field.validationExpression.trim() !== "";
  return field.required === true || hasValidation;
};

/**
 * Reads the selection straight off `panels`, which is what the UI renders from.
 * `defaultValue` cannot be trusted here: the panel handlers rewrite it from the
 * full `testOptions` list or from stale state, so it stays non-empty after every
 * test has been deselected.
 */
export const sectionsHaveTestSelection = (sections: any): boolean => {
  if (!Array.isArray(sections)) return true;
  let hasTestSelectionField = false;

  for (const section of sections) {
    if (!Array.isArray(section?.fields)) continue;
    for (const field of section.fields) {
      if (!isRequiredTestSelectionField(field)) continue;
      hasTestSelectionField = true;
      if (field.panels.some(isPanelSelected)) return true;
    }
  }

  // Requisitions without a required testing-option field cannot be gated on it.
  return !hasTestSelectionField;
};

/** Selected requisitions that still have no test or panel picked. */
export const getRequisitionsMissingTestSelection = (infectiousData: any) => {
  if (!Array.isArray(infectiousData)) return [];
  return infectiousData.filter(
    (item: any) => item?.isSelected && !sectionsHaveTestSelection(item?.sections)
  );
};
